import { useEffect, useState } from 'react';

/**
 * True while the viewport matches a CSS media query; re-evaluates live on
 * resize and rotation.
 *
 * Pre-P8 punch list, Tier 2 (mobile layout): every style in this app is an
 * inline style object, and inline styles cannot carry @media rules — so
 * until now the codebase had NO responsive behaviour at all ("no @media
 * queries exist anywhere in this codebase"). Two honest ways to fix that:
 * introduce a stylesheet and class names alongside the inline objects, or
 * read the breakpoint in JS and pick the style object. This hook is the
 * second, chosen because it keeps every component's styling in ONE place
 * (its own `S` object, the existing convention) instead of splitting it
 * across a file the components never reference. The cost is that the first
 * paint uses the initial matchMedia() value — fine here, since it is read
 * synchronously before render, not after.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    // Re-sync in case the query changed between the initial read and the
    // subscription (a different `query` argument on re-render).
    setMatches(mql.matches);
    // Safari < 14 only has the deprecated addListener/removeListener pair; a
    // throw here would surface as a whole-view crash (review finding).
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

/**
 * The app's ONE breakpoint. Below this the layout treats the device as a
 * phone: single-column tables become scroll containers or stacked rows, the
 * calendar switches to its compact rendering, and so on. 768px is the
 * conventional tablet-portrait line, and matches the punch list's own
 * "<768px" wording.
 */
export const NARROW_QUERY = '(max-width: 767px)';

export function useIsNarrow(): boolean {
  return useMediaQuery(NARROW_QUERY);
}
